import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { InternalPricingCard } from '../components/InternalPricingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Sparkles, ShoppingBag, Plus, Trash2, Search, Layers, Package, Palette, Copy, UserCheck, Scissors } from 'lucide-react';

const PRINT_METHODS = [
  { value: 'screen_print', label: 'Screen Print' },
  { value: 'embroidery', label: 'Embroidery' },
  { value: 'dtf', label: 'DTF (Direct to Film)' },
  { value: 'dtg', label: 'DTG (Direct to Garment)' },
  { value: 'sublimation', label: 'Sublimation' }
];

const PRINT_LOCATIONS = ['Front', 'Back', 'Left Chest', 'Right Chest', 'Left Sleeve', 'Right Sleeve'];

const DEFAULT_SIZES = { S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 };
// Shared item mapping (preview + submit). Garment sell prices are computed server-side from base_price/size_costs × company markup.
const toProposalItems = (designGroups) => {
  let allItems = [];
  let totalQuantity = 0;

  for (const group of designGroups) {
    for (const item of group.items) {
      // For CPP items, base_price and markup_price are $0.
      // For Cut & Sew items the admin sets a flat per-shirt price that
      // includes everything — we promote it to base_price/markup_price
      // and forward it to the backend as `custom_price` so the AI-side
      // pricing helper applies the same lock-and-skip behaviour.
      const isCPP = item.isCPP === true;
      const isCutSew = item.isCutSew === true;
      let basePrice;
      let markupPrice;
      if (isCutSew) {
        const cp = parseFloat(item.customPrice || 0);
        basePrice = cp;
        markupPrice = cp;
      } else if (isCPP) {
        basePrice = 0;
        markupPrice = 0;
      } else {
        basePrice = item.basePrice || item.customerPrice || 3.50;
        markupPrice = 0; // server applies company garment markup (default 2.1×) to actual per-size wholesale
      }

      allItems.push({
        brand: item.brandName,
        style: item.styleName,
        styleID: item.styleID,
        color: item.selectedColor,
        sizes: item.sizes,
        base_price: basePrice,
        markup_price: markupPrice,
        size_costs: item.sizeCosts || undefined,
        image_url: item.styleImage,
        quantity: item.quantity,
        supplier: item.supplier,
        is_cpp: isCPP, // Customer Provided Product flag
        is_cut_sew: isCutSew,
        custom_price: isCutSew ? parseFloat(item.customPrice || 0) : undefined,
        print_cost_locked: isCutSew, // self-heal must leave cut-and-sew alone
        // Design group info
        design_group: group.name,
        design_group_id: group.id,
        design_description: group.description,
        print_method: isCutSew ? 'cut_and_sew' : group.printMethod,
        print_locations: isCutSew ? [] : group.printLocations,
        print_colors: isCutSew ? 0 : group.colorsPerLocation
      });
      totalQuantity += item.quantity;
    }
  }

  // Build design groups summary for proposal
  const designGroupsSummary = designGroups.map(g => ({
    name: g.name,
    description: g.description,
    print_method: g.printMethod,
    print_locations: g.printLocations,
    colors_per_location: g.colorsPerLocation,
    item_count: g.items.length,
    total_quantity: g.items.reduce((sum, i) => sum + i.quantity, 0),
    has_cpp_items: g.items.some(i => i.isCPP === true),
    has_artwork: !!g.logoImage
  }));
  return { items: allItems, totalQuantity, designGroupsSummary };
};


const QuoteBuilder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Customer Info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  
  // Design Groups - each group shares a design and setup fees
  const [designGroups, setDesignGroups] = useState([{
    id: Date.now(),
    name: 'Design 1',
    description: '',
    printMethod: 'screen_print',
    printLocations: ['Front'],
    colorsPerLocation: { 'Front': 1 },
    logoImage: null,
    items: [] // Products in this design group
  }]);
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  
  // Pricing preview
  const [pricing, setPricing] = useState(null);
  const [garmentMarkup, setGarmentMarkup] = useState(2.1);
  useEffect(() => { axios.get('/pricing/policy').then((r) => setGarmentMarkup(r.data.garment_markup || 2.1)).catch(() => {}); }, []);

  // Load products from sessionStorage (from Products page)
  useEffect(() => {
    const storedProducts = sessionStorage.getItem('selectedProducts');
    if (storedProducts) {
      try {
        const products = JSON.parse(storedProducts);
        const productsWithDetails = products.map(p => ({
          ...p,
          id: Date.now() + Math.random(),
          quantity: 24,
          selectedColor: p.colors?.[0]?.colorName || 'Black',
          sizes: { ...DEFAULT_SIZES }
        }));
        
        // Add to first design group
        setDesignGroups(prev => [{
          ...prev[0],
          items: productsWithDetails
        }]);
        
        sessionStorage.removeItem('selectedProducts');
        toast.success(`${products.length} product(s) added to Design 1`);
      } catch (e) {
        console.error('Failed to parse stored products:', e);
      }
    }
  }, []);

  // Search products
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      // Try multi-vendor search first
      const response = await axios.get(`/products/search/all/${encodeURIComponent(query)}`);
      setSearchResults(Array.isArray(response.data) ? response.data.slice(0, 8) : [response.data]);
    } catch (error) {
      // Fallback to regular search
      try {
        const response = await axios.get(`/products?search=${encodeURIComponent(query)}&use_live=true`);
        setSearchResults(response.data.slice(0, 8));
      } catch (e) {
        setSearchResults([]);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const debounce = setTimeout(() => {
      searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(debounce);
  }, [productSearch]);

  // Add product to a design group
  const addProductToGroup = async (groupId, product) => {
    // Pull actual per-size wholesale (SanMar / S&S) so 2XL+ are priced from real cost, never a flat surcharge.
    let sizeCosts = product.sizePrices || null;
    if (!sizeCosts) {
      try { const { data } = await axios.get('/pricing/size-costs', { params: { style: product.styleID, supplier: product.supplier || '' } }); sizeCosts = data.size_costs && Object.keys(data.size_costs).length ? data.size_costs : null; } catch (e) { sizeCosts = null; }
    }
    const newProduct = {
      ...product,
      id: Date.now() + Math.random(),
      quantity: 24,
      selectedColor: product.colors?.[0]?.colorName || 'Black',
      sizes: { ...DEFAULT_SIZES },
      sizeCosts,
      isCPP: false // Not a customer provided product
    };
    
    setDesignGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, items: [...g.items, newProduct] }
        : g
    ));
    
    setProductSearch('');
    setSearchResults([]);
    setActiveGroupId(null);
    toast.success(`Added ${product.brandName} ${product.styleID} to design group`);
  };

  // Add CPP (Customer Provided Product) to a design group
  const addCPPToGroup = (groupId) => {
    const newCPP = {
      id: Date.now() + Math.random(),
      brandName: 'Customer Provided',
      styleID: 'CPP',
      styleName: '',
      selectedColor: 'Various',
      colors: [{ colorName: 'Various' }, { colorName: 'Black' }, { colorName: 'White' }, { colorName: 'Navy' }],
      basePrice: 0,
      customerPrice: 0,
      quantity: 24,
      sizes: { ...DEFAULT_SIZES },
      supplier: 'Customer',
      isCPP: true,
      cppDescription: '' // Description of the customer's product
    };
    
    setDesignGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, items: [...g.items, newCPP] }
        : g
    ));
    
    setActiveGroupId(null);
    toast.success('Customer Provided Product added - decoration costs only');
  };

  // Add a Cut & Sew custom-priced item to a design group. Unlike a catalog
  // product, the all-in per-shirt price lives directly on the item; no 2.08×
  // markup is applied and no decoration matrix is run. Mirrors the AI
  // assistant's `custom_price` flow so the backend produces identical math.
  const addCutSewToGroup = (groupId) => {
    const newCutSew = {
      id: Date.now() + Math.random(),
      brandName: 'Custom Cut & Sew',
      styleID: 'CUSTOM',
      styleName: 'Cut & Sew Shirt',
      selectedColor: 'As shown',
      colors: [{ colorName: 'As shown' }],
      basePrice: 0,
      customerPrice: 0,
      quantity: 12,
      sizes: { ...DEFAULT_SIZES, L: 12 },
      supplier: 'Custom',
      isCutSew: true,
      customPrice: 38, // Default — admin edits per order
      cppDescription: 'Cut & sew shirt (off-catalog)',
    };
    setDesignGroups(prev => prev.map(g =>
      g.id === groupId
        ? { ...g, items: [...g.items, newCutSew] }
        : g
    ));
    setActiveGroupId(null);
    toast.success('Cut & Sew item added — set the per-shirt price below');
  };

  // Remove product from group
  const removeProductFromGroup = (groupId, productId) => {
    setDesignGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, items: g.items.filter(item => item.id !== productId) }
        : g
    ));
  };

  // Update product in group
  const updateProductInGroup = (groupId, productId, field, value) => {
    setDesignGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      return {
        ...g,
        items: g.items.map(item => {
          if (item.id !== productId) return item;
          if (field === 'sizes') {
            const sizes = { ...item.sizes, ...value };
            const sum = Object.values(sizes).reduce((a, n) => a + (parseInt(n) || 0), 0);
            // Size breakdown drives quantity so garments, print tier and setup all price the same count
            return { ...item, sizes, quantity: sum > 0 ? sum : item.quantity };
          }
          return { ...item, [field]: value };
        })
      };
    }));
  };

  // Add new design group
  const addDesignGroup = () => {
    const newGroup = {
      id: Date.now(),
      name: `Design ${designGroups.length + 1}`,
      description: '',
      printMethod: 'screen_print',
      printLocations: ['Front'],
      colorsPerLocation: { 'Front': 1 },
      logoImage: null,
      items: []
    };
    setDesignGroups([...designGroups, newGroup]);
    toast.success('New design group added');
  };

  // Remove design group
  const removeDesignGroup = (groupId) => {
    if (designGroups.length <= 1) {
      toast.error('Must have at least one design group');
      return;
    }
    setDesignGroups(designGroups.filter(g => g.id !== groupId));
  };

  // Duplicate design group
  const duplicateDesignGroup = (groupId) => {
    const group = designGroups.find(g => g.id === groupId);
    if (!group) return;
    
    const newGroup = {
      ...group,
      id: Date.now(),
      name: `${group.name} (Copy)`,
      items: group.items.map(item => ({ ...item, id: Date.now() + Math.random() }))
    };
    setDesignGroups([...designGroups, newGroup]);
    toast.success('Design group duplicated');
  };

  // Update design group settings
  const updateGroupSettings = (groupId, field, value) => {
    setDesignGroups(prev => prev.map(g => 
      g.id === groupId ? { ...g, [field]: value } : g
    ));
  };

  // Toggle print location for group
  const toggleGroupLocation = (groupId, location) => {
    setDesignGroups(prev => prev.map(g => {
      if (g.id !== groupId) return g;
      
      const hasLocation = g.printLocations.includes(location);
      let newLocations, newColors;
      
      if (hasLocation) {
        newLocations = g.printLocations.filter(l => l !== location);
        newColors = { ...g.colorsPerLocation };
        delete newColors[location];
      } else {
        newLocations = [...g.printLocations, location];
        newColors = { ...g.colorsPerLocation, [location]: 1 };
      }
      
      return { ...g, printLocations: newLocations, colorsPerLocation: newColors };
    }));
  };

  // Update colors for location
  const updateGroupLocationColors = (groupId, location, colors) => {
    setDesignGroups(prev => prev.map(g => 
      g.id === groupId 
        ? { ...g, colorsPerLocation: { ...g.colorsPerLocation, [location]: parseInt(colors) || 1 } }
        : g
    ));
  };

  // State for color analysis
  const [colorAnalysis, setColorAnalysis] = useState({});
  const [analyzingColors, setAnalyzingColors] = useState(false);

  // Handle logo upload for group
  const handleGroupLogoUpload = async (groupId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      updateGroupSettings(groupId, 'logoImage', base64);
      toast.success('Logo uploaded! Analyzing colors...');
      
      // Analyze colors
      setAnalyzingColors(true);
      try {
        const formData = new FormData();
        formData.append('logo_base64', base64);
        const response = await axios.post('/analyze-logo-colors', formData);
        
        if (response.data.success) {
          setColorAnalysis(prev => ({
            ...prev,
            [groupId]: response.data
          }));
          
          // Auto-set colors per location based on analysis
          const colorCount = response.data.color_count;
          const group = designGroups.find(g => g.id === groupId);
          if (group && group.printLocations.length > 0) {
            const newColorsPerLocation = {};
            group.printLocations.forEach(loc => {
              newColorsPerLocation[loc] = colorCount;
            });
            updateGroupSettings(groupId, 'colorsPerLocation', newColorsPerLocation);
          }
          
          toast.success(`Found ${colorCount} color${colorCount !== 1 ? 's' : ''} in your logo!`);
        }
      } catch (error) {
        console.error('Color analysis failed:', error);
      } finally {
        setAnalyzingColors(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Pricing preview — served by POST /pricing/preview, the exact code path POST /proposals uses
  // (company garment markup × actual per-size wholesale + the decoration matrices), so preview == saved proposal.
  useEffect(() => {
    const totalItems = designGroups.reduce((sum, g) => sum + g.items.length, 0);
    if (totalItems === 0) { setPricing(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { items, totalQuantity, designGroupsSummary } = toProposalItems(designGroups);
        const { data } = await axios.post('/pricing/preview', { items, quantity: totalQuantity, design_groups: designGroupsSummary, tax_exempt: false });
        setPricing({ subtotal: data.pricing.subtotal, setupFees: data.pricing.setup_fees, tax: data.pricing.tax_amount, total: data.pricing.total_amount,
          quantity: totalQuantity, hasCPPItems: items.some((i) => i.is_cpp), garmentMarkup: data.garment_markup, internal: data.internal, items: data.items });
      } catch (e) { console.warn('pricing preview failed', e); }
    }, 300);
    return () => clearTimeout(t);
  }, [designGroups]);


  // Submit proposal
  const handleSubmit = async () => {
    if (!customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }

    const totalItems = designGroups.reduce((sum, g) => sum + g.items.length, 0);
    if (totalItems === 0) {
      toast.error('Please add at least one product');
      return;
    }

    const hasEmptyLocations = designGroups.some(g => g.printLocations.length === 0);
    if (hasEmptyLocations) {
      toast.error('Please select at least one print location for each design group');
      return;
    }

    setLoading(true);
    try {
      // Build items with design group info
      const { items: allItems, totalQuantity, designGroupsSummary } = toProposalItems(designGroups);

      // Get design description from first group for AI mockup generation
      const designDescription = designGroups[0]?.description || '';

      const proposalData = {
        customer_name: customerName,
        customer_email: (customerEmail || "").trim() || undefined,
        customer_phone: customerPhone,
        quantity: totalQuantity,
        items: allItems,
        design_groups: designGroupsSummary,
        design_description: designDescription, // For AI mockup generation
        print_details: {
          // Legacy format for backward compatibility
          locations: designGroups[0]?.printLocations || [],
          colors_per_location: designGroups[0]?.colorsPerLocation || {},
          method: designGroups[0]?.printMethod || 'screen_print'
        },
        notes: notes
      };

      const response = await axios.post('/proposals', proposalData);
      const proposalId = response.data.id;
      
      // Upload logos for each design group
      for (const group of designGroups) {
        if (group.logoImage) {
          try {
            const formData = new FormData();
            formData.append('logo_base64', group.logoImage);
            formData.append('design_group', group.name);
            await axios.put(`/proposals/${proposalId}/logo`, formData);
          } catch (error) {
            console.error(`Failed to upload logo for ${group.name}:`, error);
          }
        }
      }

      // Generate mockups
      try {
        await axios.post(`/proposals/${proposalId}/generate-mockups`);
      } catch (error) {
        console.error('Failed to generate mockups:', error);
      }
      
      toast.success('Proposal created successfully!');
      navigate(`/proposal/${proposalId}`);
    } catch (error) {
      const d = error.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join('; ') : (typeof d === 'string' ? d : 'Failed to create proposal'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quote-builder">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Proposal</h1>
            <p className="text-gray-600">Build a quote with multiple designs and styles</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Customer Name *</Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Smith"
                    className="mt-1"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="john@company.com"
                    className="mt-1"
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                    data-testid="customer-phone-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Design Groups */}
            {designGroups.map((group, groupIndex) => (
              <Card key={group.id} className="border-2 border-gray-200">
                <CardHeader className="bg-gray-50 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Layers className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <Input
                          value={group.name}
                          onChange={(e) => updateGroupSettings(group.id, 'name', e.target.value)}
                          className="font-semibold text-lg border-0 p-0 h-auto bg-transparent focus-visible:ring-0"
                          placeholder="Design Name"
                        />
                        <p className="text-sm text-gray-500">
                          {group.items.length} style(s) • {group.items.reduce((s, i) => s + i.quantity, 0)} total pcs
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => duplicateDesignGroup(group.id)}
                        title="Duplicate design group"
                      >
                        <Copy size={16} />
                      </Button>
                      {designGroups.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeDesignGroup(group.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  {/* Design Description */}
                  <div>
                    <Label>Design Description (for AI mockup generation)</Label>
                    <Input
                      value={group.description}
                      onChange={(e) => updateGroupSettings(group.id, 'description', e.target.value)}
                      placeholder="e.g., Red company logo with white text on chest"
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Describe your design - AI will use this to create realistic mockups</p>
                  </div>

                  {/* Artwork Upload Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label className="text-blue-800 font-medium flex items-center gap-2">
                      <Palette size={16} />
                      Artwork / Logo Upload
                    </Label>
                    <p className="text-xs text-blue-600 mt-1 mb-3">Upload your artwork to overlay on the AI-generated mockup</p>
                    
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 hover:border-blue-400 transition-all bg-white">
                          <Upload size={20} className="text-blue-500" />
                          <span className="text-sm font-medium text-blue-700">
                            {analyzingColors ? 'Analyzing colors...' : group.logoImage ? '✓ Artwork uploaded - Click to change' : 'Click to upload artwork'}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleGroupLogoUpload(group.id, e)}
                            className="hidden"
                            disabled={analyzingColors}
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">Supported: PNG, JPG, SVG (transparent PNG recommended)</p>
                      </div>
                      
                      {/* Artwork Preview */}
                      {group.logoImage && (
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 border-2 border-blue-300 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                            <img 
                              src={`data:image/png;base64,${group.logoImage}`} 
                              alt="Artwork preview" 
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <button
                            onClick={() => {
                              updateGroupSettings(group.id, 'logoImage', null);
                              setColorAnalysis(prev => {
                                const newState = {...prev};
                                delete newState[group.id];
                                return newState;
                              });
                            }}
                            className="text-xs text-red-500 hover:text-red-700 mt-1 w-full text-center"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Color Analysis Results */}
                    {colorAnalysis[group.id] && colorAnalysis[group.id].colors && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm text-gray-800">
                            🎨 {colorAnalysis[group.id].color_count} Color{colorAnalysis[group.id].color_count !== 1 ? 's' : ''} Detected
                          </span>
                          {colorAnalysis[group.id].color_count > 3 && group.printMethod === 'screen_print' && (
                            <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                              Consider DTF for fewer setup costs
                            </span>
                          )}
                        </div>
                        
                        {/* Color swatches */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {colorAnalysis[group.id].colors.map((color, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-gray-50 rounded px-2 py-1">
                              <div 
                                className="w-4 h-4 rounded border border-gray-300"
                                style={{ backgroundColor: color.hex }}
                              />
                              <span className="text-xs text-gray-700">{color.name}</span>
                              <span className="text-xs text-gray-400">({color.percentage}%)</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Recommendation */}
                        <p className="text-xs text-gray-600 italic">
                          💡 {colorAnalysis[group.id].recommendation}
                        </p>
                        
                        {/* Option to reduce colors */}
                        {colorAnalysis[group.id].color_count > 2 && group.printMethod === 'screen_print' && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-2">Adjust colors for print locations:</p>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-600">Use</span>
                              <Select
                                value={String(Object.values(group.colorsPerLocation)[0] || colorAnalysis[group.id].color_count)}
                                onValueChange={(v) => {
                                  const newColors = {};
                                  group.printLocations.forEach(loc => {
                                    newColors[loc] = parseInt(v);
                                  });
                                  updateGroupSettings(group.id, 'colorsPerLocation', newColors);
                                }}
                              >
                                <SelectTrigger className="w-20 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3, 4, 5, 6].filter(n => n <= colorAnalysis[group.id].color_count).map(n => (
                                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <span className="text-xs text-gray-600">color{Object.values(group.colorsPerLocation)[0] !== 1 ? 's' : ''} for screen print</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Print Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Print Method</Label>
                      <Select 
                        value={group.printMethod} 
                        onValueChange={(v) => updateGroupSettings(group.id, 'printMethod', v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRINT_METHODS.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Print Locations */}
                  <div>
                    <Label className="mb-2 block">Print Locations & Colors</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {PRINT_LOCATIONS.map(location => (
                        <div 
                          key={location}
                          className={`border rounded-lg p-3 cursor-pointer transition-all ${
                            group.printLocations.includes(location) 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleGroupLocation(group.id, location)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{location}</span>
                            {group.printLocations.includes(location) && (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <Palette size={14} className="text-purple-600" />
                                <Select
                                  value={String(group.colorsPerLocation[location] || 1)}
                                  onValueChange={(v) => updateGroupLocationColors(group.id, location, v)}
                                >
                                  <SelectTrigger className="h-7 w-16 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {[1,2,3,4,5,6].map(n => (
                                      <SelectItem key={n} value={String(n)}>{n} color{n>1?'s':''}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Products/Styles in this group */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Styles in this Design Group</Label>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addCPPToGroup(group.id)}
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          title="Customer Provided Product - decoration only"
                        >
                          <UserCheck size={14} className="mr-1" />
                          Add CPP
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addCutSewToGroup(group.id)}
                          className="text-rose-600 border-rose-300 hover:bg-rose-50"
                          title="Custom Cut & Sew — flat per-shirt price, no catalog lookup"
                          data-testid={`add-cut-sew-${group.id}`}
                        >
                          <Scissors size={14} className="mr-1" />
                          Cut &amp; Sew
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveGroupId(activeGroupId === group.id ? null : group.id)}
                          data-testid={`add-style-${group.id}`}
                        >
                          <Plus size={14} className="mr-1" />
                          Add Style
                        </Button>
                      </div>
                    </div>

                    {/* Product Search (shown when active) */}
                    {activeGroupId === group.id && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                          <Input
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value.toUpperCase())}
                            placeholder="Search by style code (e.g., ST350, G500)..."
                            className="pl-9"
                            data-testid={`product-search-input-${group.id}`}
                            autoFocus
                          />
                        </div>
                        {searchLoading && (
                          <p className="text-sm text-gray-500 mt-2">Searching...</p>
                        )}
                        {searchResults.length > 0 && (
                          <div className="mt-2 border rounded-md divide-y max-h-60 overflow-auto bg-white">
                            {searchResults.map((product, idx) => (
                              <div
                                key={idx}
                                className="p-3 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                                data-testid={`product-search-result-${idx}`}
                                onClick={() => addProductToGroup(group.id, product)}
                              >
                                <div>
                                  <p className="font-medium">{product.brandName} {product.styleID}</p>
                                  <p className="text-sm text-gray-500">{product.styleName}</p>
                                  <p className="text-xs text-gray-400">
                                    {product.supplier} • {product.colors?.length || 0} colors • wholesale {formatCurrency(product.basePrice || 0)} → sells {formatCurrency((product.basePrice || 0) * garmentMarkup)}
                                  </p>
                                </div>
                                <Button size="sm" variant="ghost">
                                  <Plus size={16} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* List of products in group */}
                    {group.items.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                        <Package className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-gray-500">No styles added yet</p>
                        <p className="text-sm text-gray-400">Click &quot;Add Style&quot; to search products or &quot;Add CPP&quot; for customer-provided items</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {group.items.map((item) => (
                          <div key={item.id} className={`border rounded-lg p-4 ${item.isCutSew ? 'bg-rose-50 border-rose-200' : item.isCPP ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                {item.isCutSew ? (
                                  <>
                                    <div className="flex items-center gap-2 mb-1">
                                      <Scissors size={16} className="text-rose-600" />
                                      <span className="text-xs font-semibold text-rose-700 bg-rose-100 px-2 py-0.5 rounded">Cut &amp; Sew · Custom Price</span>
                                    </div>
                                    <Input
                                      value={item.styleName}
                                      onChange={(e) => updateProductInGroup(group.id, item.id, 'styleName', e.target.value)}
                                      placeholder="Describe the cut & sew item (e.g., Custom Mesh Jersey)"
                                      className="font-semibold border-rose-200 bg-white"
                                      data-testid={`cutsew-style-${item.id}`}
                                    />
                                    <div className="flex items-center gap-2 mt-2">
                                      <Label className="text-xs text-rose-700 font-semibold whitespace-nowrap">
                                        $ / shirt
                                      </Label>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min={0}
                                        value={item.customPrice ?? ''}
                                        onChange={(e) => updateProductInGroup(group.id, item.id, 'customPrice', parseFloat(e.target.value) || 0)}
                                        placeholder="38.00"
                                        className="h-8 w-28 bg-white border-rose-300 font-semibold text-rose-900"
                                        data-testid={`cutsew-price-${item.id}`}
                                      />
                                      <span className="text-xs text-rose-600">
                                        all-in (no markup, no decoration math)
                                      </span>
                                    </div>
                                  </>
                                ) : item.isCPP ? (
                                  <>
                                    <div className="flex items-center gap-2 mb-1">
                                      <UserCheck size={16} className="text-amber-600" />
                                      <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">CPP - Customer Provided</span>
                                    </div>
                                    <Input
                                      value={item.styleName}
                                      onChange={(e) => updateProductInGroup(group.id, item.id, 'styleName', e.target.value)}
                                      placeholder="Describe the product (e.g., Nike Dri-FIT Polo)"
                                      className="font-semibold border-amber-200 bg-white"
                                    />
                                    <p className="text-xs text-amber-600 mt-1">$0.00/pc (decoration only)</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-semibold">{item.brandName} {item.styleID}</p>
                                    <p className="text-sm text-gray-500">{item.styleName}</p>
                                    <p className="text-xs text-gray-400">{item.supplier} • {formatCurrency((item.basePrice || 0) * garmentMarkup)}/pc{item.sizeCosts ? ' • real size costs' : ''}</p>
                                  </>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProductFromGroup(group.id, item.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div>
                                <Label className="text-xs">Color</Label>
                                {(item.isCPP || item.isCutSew) ? (
                                  <Input
                                    value={item.selectedColor}
                                    onChange={(e) => updateProductInGroup(group.id, item.id, 'selectedColor', e.target.value)}
                                    placeholder="e.g., Black, Navy, As shown"
                                    className="mt-1 h-9"
                                  />
                                ) : (
                                  <Select
                                    value={item.selectedColor}
                                    onValueChange={(v) => updateProductInGroup(group.id, item.id, 'selectedColor', v)}
                                  >
                                    <SelectTrigger className="mt-1 h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(item.colors || []).map(c => (
                                        <SelectItem key={c.colorName} value={c.colorName}>{c.colorName}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                              <div>
                                <Label className="text-xs">Quantity</Label>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateProductInGroup(group.id, item.id, 'quantity', parseInt(e.target.value) || 0)}
                                  className="mt-1 h-9"
                                  data-testid="item-quantity-input"
                                  min={1}
                                />
                              </div>
                            </div>

                            {/* Size breakdown */}
                            <div className="mt-3">
                              <Label className="text-xs mb-1 block">Size Breakdown (optional)</Label>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(DEFAULT_SIZES).map(size => (
                                  <div key={size} className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500 w-8">{size}</span>
                                    <Input
                                      type="number"
                                      value={item.sizes[size] || ''}
                                      onChange={(e) => updateProductInGroup(group.id, item.id, 'sizes', { [size]: parseInt(e.target.value) || 0 })}
                                      className="w-14 h-7 text-xs"
                                      data-testid={`size-input-${size}`}
                                      min={0}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Add Design Group Button */}
            <Button
              variant="outline"
              className="w-full py-6 border-dashed border-2"
              onClick={addDesignGroup}
            >
              <Plus size={20} className="mr-2" />
              Add Another Design Group
            </Button>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions or notes for this proposal..."
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Pricing Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <Card>
                <CardHeader className="bg-gray-900 text-white rounded-t-lg">
                  <CardTitle className="text-lg">Quote Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {pricing ? (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Quantity</span>
                        <span className="font-medium" data-testid="qb-total-qty">{pricing.quantity} pcs</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Design Groups</span>
                        <span className="font-medium">{designGroups.length}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span data-testid="qb-subtotal">{formatCurrency(pricing.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Setup Fees</span>
                        <span data-testid="qb-setup-fees">{formatCurrency(pricing.setupFees)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax (7%)</span>
                        <span data-testid="qb-tax">{formatCurrency(pricing.tax)}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total</span>
                        <span className="text-green-600" data-testid="qb-total">{formatCurrency(pricing.total)}</span>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        * Setup fees are shared within each design group · garment prices = actual wholesale × {garmentMarkup}
                      </p>
                      {pricing.internal && <InternalPricingCard internal={pricing.internal} compact />}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      Add products to see pricing
                    </p>
                  )}
                  
                  <Button
                    className="w-full mt-6 bg-green-600 hover:bg-green-700"
                    onClick={handleSubmit}
                    disabled={loading || !customerName || designGroups.every(g => g.items.length === 0)}
                    data-testid="create-proposal-button"
                  >
                    {loading ? (
                      <>Creating Proposal...</>
                    ) : (
                      <>
                        <Sparkles size={18} className="mr-2" />
                        Create Proposal
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Tips */}
              <Card className="mt-4 bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <h4 className="font-semibold text-blue-900 mb-2">💡 Design Groups</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Each design group shares one artwork/logo</li>
                    <li>• Setup fees are shared within a group</li>
                    <li>• Add multiple styles to offer options</li>
                    <li>• Duplicate groups to reuse settings</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;
